import type { JSX, ReactNode } from 'react';
import type {
  TooltipProps as TooltipRootProps,
  TooltipContentProps,
} from '@radix-ui/react-tooltip';
import {
  Root as TooltipRoot,
  Content as TooltipContent,
  Trigger as TooltipTrigger,
  Portal as TooltipPortal,
  Arrow as TooltipArrow,
} from '@radix-ui/react-tooltip';
import { styled, keyframes } from 'styled-components';

type StyledContentProps = TooltipContentProps;

const hide = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const slideDownAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideLeftAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateX(6px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideUpAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideRightAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateX(-6px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const StyledContent = styled(TooltipContent)<StyledContentProps>`
  --tooltip-font-family: var(--wui-typography-family-default);
  --tooltip-border-radius: var(--wui-border-radius-05);
  --tooltip-bg: var(--wui-color-bg-tooltip);
  --tooltip-color: var(--wui-color-text-on-fill);
  --tooltip-font-size: var(--wui-typography-label-4-size);
  --tooltip-line-height: var(--wui-typography-label-4-line-height);
  --tooltip-font-weight: var(--wui-typography-label-4-weight);
  --tooltip-horizontal-padding: var(--wui-space-02);
  --tooltip-vertical-padding: var(--wui-space-03);
  --tooltip-animation-duration: var(--wui-motion-duration-04);
  --tooltip-animation-timing-function: var(--wui-motion-ease-out);

  font-family: var(--tooltip-font-family);
  font-size: var(--tooltip-font-size);
  border-radius: var(--tooltip-border-radius);
  padding: var(--tooltip-horizontal-padding) var(--tooltip-vertical-padding);
  background-color: var(--tooltip-bg);
  color: var(--tooltip-color);
  user-select: none;
  animation-duration: var(--tooltip-animation-duration);
  animation-timing-function: var(--tooltip-animation-timing-function);
  will-change: transform, opacity;
  max-width: 30em;
  z-index: var(--wui-zindex-tooltip);

  &[data-state='closed'] {
    animation-name: ${hide};
  }

  &[data-state='delayed-open'][data-side='top'] {
    animation-name: ${slideUpAndFade};
  }

  &[data-state='delayed-open'][data-side='right'] {
    animation-name: ${slideRightAndFade};
  }

  &[data-state='delayed-open'][data-side='bottom'] {
    animation-name: ${slideDownAndFade};
  }

  &[data-state='delayed-open'][data-side='left'] {
    animation-name: ${slideLeftAndFade};
  }
`;

export type TooltipProps = {
  /**
   * The content of the tooltip
   */
  content: ReactNode;
  /**
   * The button or other interactive element that triggers the tooltip
   */
  children: ReactNode;
  /**
   * The delay in milliseconds before the tooltip appears
   */
  delay?: number;
  /**
   * The preferred direction to render the tooltip if no collisions prevent it
   */
  direction?: 'bottom' | 'left' | 'right' | 'top';
  /**
   * @ignore
   * Force the tooltip to be open without user interaction (for debugging and CI)
   */
  forceOpen?: boolean;
  /**
   * Hides the arrow
   */
  hideArrow?: boolean;
  /**
   * Specify a container element to portal the tooltip into.
   * Useful for rendering tooltips in contexts like pip windows.
   * @default document.body
   */
  container?: Element | null;
};

const VISUAL_OFFSET = 6;

/**
 * Tooltips are a way to provide additional information or context to a user when they hover or focus an element.
 * For elements that are not interactive, consider using a [Popover]() instead.
 *
 */
export const Tooltip = ({
  delay = 500,
  direction = 'top',
  content,
  children,
  forceOpen,
  hideArrow = false,
  container = null,
}: TooltipProps): JSX.Element => {
  const rootProps: TooltipRootProps = {};

  if (content === '' || content === null) {
    rootProps.open = false;
  }

  if (forceOpen === true) {
    rootProps.open = true;
  }

  return (
    <TooltipRoot
      delayDuration={delay}
      {...rootProps}
    >
      <TooltipTrigger asChild={true}>{children}</TooltipTrigger>
      <TooltipPortal container={container}>
        <StyledContent
          side={direction}
          sideOffset={hideArrow ? VISUAL_OFFSET : VISUAL_OFFSET - 2}
        >
          {content}
          {!hideArrow ? (
            <TooltipArrow asChild={true}>
              <svg
                fill="var(--tooltip-bg)"
                height="8"
                style={{ transform: 'translateY(-2px)' }}
                viewBox="0 0 12 8"
                width="12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M6.8 6.93333C6.4 7.46667 5.6 7.46667 5.2 6.93333L-2.54292e-07 -1.04907e-06L12 0L6.8 6.93333Z" />
              </svg>
            </TooltipArrow>
          ) : null}
        </StyledContent>
      </TooltipPortal>
    </TooltipRoot>
  );
};

Tooltip.displayName = 'Tooltip';
